import { Outlet, useLocation } from 'react-router-dom';

import { useSignUpReminder } from '@/compositions/hooks/useSignUpReminder';
import { HOME_ROUTE } from '@/config/navigation';
import { cn } from '@/utils/helpers/cn';

import { BottomNav } from './BottomNav';
import { SignUpReminder } from './SignUpReminder';

/**
 * Shell das telas autenticáveis: conteúdo + barra inferior.
 *
 * A barra vive **aqui**, e não dentro de cada página, por dois motivos:
 *
 * 1. Com a barra em cada página, o estado de aba ativa se duplica em cinco
 *    lugares e diverge no primeiro item novo.
 * 2. Numa rota de layout a barra não remonta a cada navegação — só o `Outlet`
 *    troca. A barra é o elemento mais estável da tela; piscar a cada toque
 *    seria o oposto do que se espera dela.
 *
 * A barra é `fixed`, então o conteúdo precisa reservar o espaço dela por baixo
 * — cada página faz isso, porque só ela sabe se rola (lista) ou não (mapa em
 * tela cheia, que desenha *sob* a barra de propósito).
 *
 * ## O aviso de cadastro incompleto mora aqui pelo mesmo motivo
 *
 * Quem pediu socorro sem conta (DV-24) precisa ver o convite em qualquer tela
 * com barra — mapa e perfil, sobretudo —, e uma cópia por página repetiria o
 * defeito do item 1. Ele flutua logo acima da barra, na zona do polegar:
 *
 * - **Reserva de rodapé.** Enquanto aparece, o invólucro ganha
 *   `data-signup-reminder`, que soma a altura dele a `--bottom-nav-inset`
 *   (`src/index.css`). As páginas continuam lendo só o token de sempre.
 *   O invólucro é `contents`: não cria caixa, então `h-dvh` e `min-h-dvh` das
 *   páginas seguem medindo a tela.
 * - **No mapa ele encolhe pela direita.** O botão de localizar fica no canto
 *   inferior direito (`right-3`, 44px, `Map.page.tsx`), na mesma altura; o
 *   `right-16` deixa os 44px dele mais um respiro. É o único acoplamento com a
 *   geometria de uma página — se o botão mudar de lugar, mude aqui junto.
 * - **`z-30`**: acima dos controles do mapa (`z-10`), abaixo da barra (`z-40`)
 *   e das folhas (`BottomSheet`), que o cobrem quando abrem.
 */
export function AppLayout() {
  const { pathname } = useLocation();
  const reminder = useSignUpReminder();
  const isMap = pathname === HOME_ROUTE;

  return (
    <>
      <div data-signup-reminder={reminder.isVisible ? '' : undefined} className="contents">
        <Outlet />
      </div>

      {reminder.isVisible ? (
        <SignUpReminder
          onDismiss={reminder.dismiss}
          className={cn(
            'fixed bottom-[var(--bottom-nav-base-inset)] left-3 z-30',
            isMap ? 'right-16' : 'right-3',
          )}
        />
      ) : null}

      <BottomNav />
    </>
  );
}
